import React, { useState, useEffect, useRef, useCallback } from 'react';
import { orgDirectoryAPI } from '../../services/api';
import { FiSearch, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';

// 404 = backend hali yangi marshrutni bilmaydi (server qayta ishga tushirilmagan).
const errText = (e) => (e.response?.status === 404
  ? "Backend yangilanmagan — serverda `sudo pm2 restart server` kerak"
  : (e.response?.data?.error || "Ma'lumotnomaga ulanib bo'lmadi"));

// Tashkilot va filial tanlash. Ma'lumot AuditApteka asosiy bazasidan keladi
// (backend `/api/org-directory`), qo'lda yozilmaydi.
//
// Oqim: tashkilotni nom yoki INN bo'yicha qidiradi → tanlaydi → shu tashkilotning
// filiallari chiqadi → filialni tanlaydi.
//
// value: { org_ref, org_name, org_inn, branch_userid, branch_name } (bo'sh bo'lsa null/`''`)
// onChange: shu shakldagi yangi qiymat bilan chaqiriladi.
// legacyCompany: eski, qo'lda yozilgan kompaniya matni — tanlanmagan bo'lsa eslatma sifatida ko'rsatiladi.

const EMPTY = { org_ref: '', org_name: '', org_inn: '', branch_userid: '', branch_name: '' };

export default function OrgBranchPicker({ value, onChange, legacyCompany }) {
  const v = { ...EMPTY, ...(value || {}) };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [error, setError] = useState(null);
  const boxRef = useRef(null);

  // Tashqariga bosilsa ro'yxat yopiladi.
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Qidiruv — har harfda emas, 250 ms kutib.
  useEffect(() => {
    if (v.org_ref || !open) return undefined;
    let alive = true;
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const res = await orgDirectoryAPI.organizations({ search: query, limit: 20 });
        if (alive) { setResults(res.data.organizations || []); setError(null); }
      } catch (e) {
        if (alive) setError(errText(e));
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(id); };
  }, [query, open, v.org_ref]);

  const loadBranches = useCallback(async (ref) => {
    setLoadingBranches(true);
    try {
      const res = await orgDirectoryAPI.branches(ref);
      setBranches(res.data.branches || []);
      setError(null);
    } catch (e) {
      setBranches([]);
      setError(errText(e));
    } finally {
      setLoadingBranches(false);
    }
  }, []);

  // Tahrirlashda tashkilot allaqachon tanlangan bo'ladi — filiallarni yuklab qo'yamiz.
  useEffect(() => {
    if (v.org_ref) loadBranches(v.org_ref);
    else setBranches([]);
  }, [v.org_ref, loadBranches]);

  const pickOrg = (org) => {
    onChange({ org_ref: org.ref, org_name: org.name, org_inn: org.inn || '', branch_userid: '', branch_name: '' });
    setOpen(false);
    setQuery('');
  };

  const clearOrg = () => {
    onChange({ ...EMPTY });
    setResults([]);
    setQuery('');
  };

  const pickBranch = (userid) => {
    const b = branches.find((x) => x.userid === userid);
    onChange({ ...v, branch_userid: b ? b.userid : '', branch_name: b ? b.name : '' });
  };

  return (
    <div ref={boxRef}>
      <label style={S.label}>Tashkilot</label>

      {v.org_ref ? (
        <div style={S.chosen}>
          <FiCheck size={14} color="#10b981" style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={S.chosenName}>{v.org_name}</div>
            {v.org_inn && <div style={S.chosenSub}>INN {v.org_inn}</div>}
          </div>
          <button type="button" style={S.clearBtn} onClick={clearOrg} title="Boshqa tashkilot tanlash">
            <FiX size={14} /> O'zgartirish
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <FiSearch size={14} style={S.searchIcon} />
          <input
            style={{ ...S.input, paddingLeft: 30 }}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Tashkilot nomi yoki INN bo'yicha qidiring"
          />
          {open && (
            <div style={S.dropdown}>
              {searching && <div style={S.hint}>Qidirilmoqda…</div>}
              {!searching && error && (
                <div style={{ ...S.hint, color: '#f59e0b' }}>
                  <FiAlertCircle size={12} /> {error}
                </div>
              )}
              {!searching && !error && results.length === 0 && (
                <div style={S.hint}>Topilmadi</div>
              )}
              {results.map((o) => (
                <button key={o.ref} type="button" style={S.option} onClick={() => pickOrg(o)}>
                  <span style={S.optName}>{o.name}</span>
                  <span style={S.optMeta}>
                    {o.inn ? `INN ${o.inn} · ` : ''}{o.branches} filial
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {v.org_ref && (
        <>
          <label style={S.label}>Filial</label>
          {loadingBranches ? (
            <div style={S.hint}>Filiallar yuklanmoqda…</div>
          ) : branches.length === 0 ? (
            <div style={{ ...S.hint, color: '#f59e0b' }}>Bu tashkilotda filial topilmadi</div>
          ) : (
            <select
              style={S.input}
              value={v.branch_userid || ''}
              onChange={(e) => pickBranch(e.target.value)}
            >
              <option value="">— filialni tanlang —</option>
              {branches.map((b) => (
                <option key={b.userid} value={b.userid}>
                  {b.name}{b.main_office ? ' (bosh ofis)' : ''}{b.active ? '' : ' — faol emas'}
                </option>
              ))}
            </select>
          )}
        </>
      )}

      {!v.org_ref && legacyCompany && (
        <div style={S.legacy}>
          Avval qo'lda yozilgan: «{legacyCompany}» — ro'yxatdan tanlansa, shu yozuv yangilanadi.
        </div>
      )}
    </div>
  );
}

const S = {
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '10px 0 5px' },
  input: {
    width: '100%', padding: '9px 11px', borderRadius: 9, boxSizing: 'border-box',
    border: '1px solid var(--border, #2d3a4f)', background: 'var(--bg-input, #0f172a)',
    color: 'var(--text-primary, #f1f5f9)', fontSize: 14, fontFamily: 'inherit',
  },
  searchIcon: { position: 'absolute', left: 10, top: 11, color: 'var(--text-muted)' },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
    background: 'var(--bg-card, #1e293b)', border: '1px solid var(--border, #2d3a4f)',
    borderRadius: 10, maxHeight: 260, overflowY: 'auto', boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
  },
  option: {
    display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px',
    background: 'transparent', border: 'none', borderBottom: '1px solid var(--border, #2d3a4f)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  optName: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #f1f5f9)' },
  optMeta: { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  hint: { padding: '9px 11px', fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 },
  chosen: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 9,
    border: '1px solid var(--border, #2d3a4f)', background: 'var(--bg-input, #0f172a)',
  },
  chosenName: {
    fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #f1f5f9)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  chosenSub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  clearBtn: {
    display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, cursor: 'pointer',
    background: 'transparent', border: '1px solid var(--border, #2d3a4f)', borderRadius: 7,
    color: 'var(--text-secondary)', fontSize: 12, padding: '4px 8px', fontFamily: 'inherit',
  },
  legacy: { marginTop: 6, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 },
};
